import { buildGraph, wouldCreateCycle, type TechniqueDto } from '@bjj/shared';
import { useMemo, useState } from 'react';

/**
 * Multi-select for prerequisites. Options that would create a circular dependency are disabled
 * up front; the API re-validates on save.
 */
export function PrerequisiteSelect({
  techniqueId,
  techniques,
  value,
  onChange,
}: {
  techniqueId: string | null;
  techniques: TechniqueDto[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const byId = useMemo(() => new Map(techniques.map((t) => [t.id, t])), [techniques]);
  const graph = useMemo(
    () => buildGraph(techniques.map((t) => ({ id: t.id, prerequisites: t.prerequisiteIds }))),
    [techniques],
  );

  const options = techniques
    .filter((t) => t.id !== techniqueId && !value.includes(t.id))
    .filter(
      (t) =>
        t.name.toLowerCase().includes(query.toLowerCase()) || t.key.includes(query.toLowerCase()),
    )
    .slice(0, 50)
    .map((t) => ({
      technique: t,
      createsCycle: techniqueId
        ? wouldCreateCycle(graph, techniqueId, [...value, t.id]) !== null
        : false,
    }));

  return (
    <div>
      <div className="chip-list" aria-label="Selected prerequisites">
        {value.length === 0 ? <span className="muted">No prerequisites</span> : null}
        {value.map((id) => (
          <span className="chip" key={id}>
            {byId.get(id)?.name ?? id}
            <button
              type="button"
              aria-label={`Remove ${byId.get(id)?.name ?? id}`}
              onClick={() => onChange(value.filter((v) => v !== id))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        type="search"
        placeholder="Search techniques to add…"
        aria-label="Search prerequisites"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div
        className="option-list"
        role="listbox"
        aria-label="Available prerequisites"
        style={{ marginTop: 6 }}
      >
        {options.length === 0 ? <div className="empty">No matching techniques</div> : null}
        {options.map(({ technique, createsCycle }) => (
          <button
            key={technique.id}
            type="button"
            role="option"
            aria-selected={false}
            disabled={createsCycle}
            title={createsCycle ? 'Would create a circular prerequisite' : undefined}
            onClick={() => onChange([...value, technique.id])}
          >
            {technique.name} <span className="muted">({technique.key})</span>
            {createsCycle ? <span className="muted"> — circular</span> : null}
          </button>
        ))}
      </div>
    </div>
  );
}
